import React, { useState } from "react";
import { Button, Input, Typography } from "antd";
import { CloseOutlined, EditOutlined } from "@ant-design/icons";
import { ContentDecision } from "../../../../../../types/healthcare/PatientAnalysisTypes";

const { Text } = Typography;

/**
 * Looks up the decision record for a given item, for tabs that need to read
 * dismissed/text state without going through DismissibleItem's render-prop
 * (e.g. to filter a list before mapping).
 */
export const getContentDecision = (
	decisions: ContentDecision[],
	itemKey: string,
): ContentDecision | undefined => decisions.find((d) => d.item_key === itemKey);

export interface DismissibleItemProps {
	/** Stable-within-this-analysis identifier for the item this decision applies to. */
	itemKey: string;
	decisions: ContentDecision[];
	onChange: (itemKey: string, patch: Partial<ContentDecision>) => void;
	/** Signed-off/finalised — dismiss/edit controls are hidden and dismissed items disappear entirely. */
	isLocked: boolean;
	/** Whether inline text-edit is offered at all — false for e.g. table rows/tags. */
	editable?: boolean;
	/** The AI-generated original text — used as both the editable default value and read-only fallback. */
	defaultText?: string;
	/** Render-prop so each tab controls its own visual layout around the (possibly edited) text. */
	children: (effectiveText: string) => React.ReactNode;
}

/**
 * Reusable dismiss/inline-edit affordance shared across every comprehensive-analysis tab.
 * Keeps its own edit-toggle state locally — parents only track the ContentDecision[] array.
 */
const DismissibleItem: React.FC<DismissibleItemProps> = ({
	itemKey,
	decisions,
	onChange,
	isLocked,
	editable = false,
	defaultText,
	children,
}) => {
	const [isEditing, setIsEditing] = useState(false);

	const decision = getContentDecision(decisions, itemKey);
	const dismissed = decision?.dismissed ?? false;
	const effectiveText = decision?.text ?? defaultText ?? "";

	// Dismissed + signed-off: fully gone.
	if (dismissed && isLocked) return null;

	// Dismissed + still in review: hidden but recoverable.
	if (dismissed) {
		return (
			<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
				<Text style={{ fontSize: 11, color: "var(--mg-text-secondary)", fontStyle: "italic" }}>
					Dismissed
				</Text>
				<Button type="text" size="small" onClick={() => onChange(itemKey, { dismissed: false })}>
					Restore
				</Button>
			</div>
		);
	}

	// Not dismissed + signed-off: plain content, no controls.
	if (isLocked) {
		return <>{children(effectiveText)}</>;
	}

	// Not dismissed + still in review: content (or inline editor) plus dismiss/edit controls.
	return (
		<div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
			<div style={{ flex: 1, minWidth: 0 }}>
				{editable && isEditing ? (
					<Input.TextArea
						value={effectiveText}
						onChange={(e) => onChange(itemKey, { text: e.target.value })}
						autoSize={{ minRows: 2 }}
						size="small"
						style={{ fontSize: 11 }}
					/>
				) : (
					children(effectiveText)
				)}
			</div>
			<div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
				{editable && (
					<Button
						type="text"
						size="small"
						icon={<EditOutlined />}
						onClick={() => setIsEditing((prev) => !prev)}
					/>
				)}
				<Button
					type="text"
					size="small"
					icon={<CloseOutlined />}
					onClick={() => onChange(itemKey, { dismissed: true })}
				/>
			</div>
		</div>
	);
};

export default DismissibleItem;
